import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, Spinner, Checkbox } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  BroomSparkle20Regular,
  PeopleSwap20Regular,
  PersonBoard20Regular,
  Home20Regular,
  History20Regular,
  BuildingMultiple20Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  Search20Regular,
  Dismiss16Regular,
  BranchCompare20Regular,
  TextboxAlignBottomRotate9020Regular,
  MailMultiple20Regular,
  MailInbox20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { useAuth } from "../context/AuthContext";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import {
  listDatenbereinigung,
  fetchDatenbereinigungRelated,
  type DatenbereinigungRow,
  type DatenbereinigungRelated,
  type DatenbereinigungMember,
} from "../services/datenbereinigungService";
import { fetchOutlookReiter } from "../services/outlookImporteService";

const PAGE_SIZE = 30;

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("10px", "14px"),
    boxShadow: "0 6px 16px rgba(45,20,43,0.06)",
  },
  searchIcon: { color: BRAND.muted, flexShrink: 0 },
  searchInput: {
    flexGrow: 1,
    ...shorthands.border("none"),
    outlineStyle: "none",
    backgroundColor: "transparent",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("4px"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg, color: BRAND.ink },
  },
  grid: {
    display: "grid",
    // Fixe 2 Spalten: Reihe 1 = Duplikate + Ansprechpartner, Reihe 2 = Haushalte.
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
  },
  card: {
    appearance: "none",
    textAlign: "left",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("20px"),
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    boxShadow: "0 6px 16px rgba(45,20,43,0.06)",
    transitionProperty: "transform, box-shadow, border-color",
    transitionDuration: "120ms",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 24px rgba(45,20,43,0.12)",
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  cardIcon: {
    width: "48px",
    height: "48px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
  },
  cardBody: { display: "flex", flexDirection: "column", rowGap: "4px", flexGrow: 1 },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    color: BRAND.ink,
  },
  cardDesc: { color: BRAND.muted, fontSize: "0.85rem" },
  chevron: { color: BRAND.muted, flexShrink: 0 },

  // --- Übersicht „Kontakte, die zu bereinigen sind" ---
  divider: {
    height: "1px",
    backgroundColor: BRAND.borderDashed,
    ...shorthands.margin("8px", "0", "0", "0"),
  },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  sectionTitle: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "1.25rem",
    color: BRAND.ink,
  },
  countChip: {
    fontSize: "0.8rem",
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    ...shorthands.padding("2px", "10px"),
    ...shorthands.borderRadius("999px"),
    fontWeight: 600,
  },
  selectAll: { display: "inline-flex", alignItems: "center", fontSize: "0.85rem" },
  selectionCount: {
    fontSize: "0.8rem",
    color: BRAND.muted,
    fontWeight: 600,
    marginLeft: "auto",
  },
  mergeBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "14px"),
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    transitionProperty: "background-color, transform",
    transitionDuration: "100ms",
    ":hover": { backgroundColor: BRAND.ink, transform: "translateY(-1px)" },
  },
  rowCheck: { display: "inline-flex", alignItems: "center", flexShrink: 0 },
  scrollBox: {
    height: "calc(100vh - 360px)",
    minHeight: "240px",
    overflowY: "auto",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    backgroundColor: BRAND.white,
  },
  rowWrap: { ...shorthands.borderBottom("1px", "solid", BRAND.bg) },
  row: {
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    ...shorthands.padding("12px", "16px"),
  },
  rowName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
  },
  rowMeta: { color: BRAND.muted, fontSize: "0.8rem" },
  rowBody: { display: "flex", flexDirection: "column", rowGap: "2px", flexGrow: 1, minWidth: 0 },
  typeBadge: {
    fontSize: "0.7rem",
    color: BRAND.muted,
    backgroundColor: BRAND.bg,
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
    marginLeft: "8px",
  },
  chips: { display: "flex", alignItems: "center", columnGap: "8px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" },
  chip: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("4px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "100ms",
    ":hover": {
      backgroundColor: "#f0e6f2",
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  empty: {
    ...shorthands.padding("28px", "16px"),
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "0.9rem",
  },
  loadMore: { textAlign: "center", color: BRAND.muted, ...shorthands.padding("12px") },
  expandBtn: {
    appearance: "none",
    cursor: "pointer",
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: BRAND.muted,
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.padding("2px"),
    flexShrink: 0,
    ":hover": { color: BRAND.purple },
  },
  expandSpacer: { width: "24px", flexShrink: 0 },
  expandPanel: {
    ...shorthands.padding("4px", "16px", "12px", "52px"),
    backgroundColor: "#faf8fb",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  expandTitle: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.purple,
    marginBottom: "2px",
  },
  expandHint: {
    fontSize: "0.82rem",
    color: BRAND.muted,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
  },
  memberRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    fontSize: "0.85rem",
    color: BRAND.ink,
  },
  memberMeta: { color: BRAND.muted, fontSize: "0.78rem" },
  bereinigtBadge: {
    fontSize: "0.68rem",
    color: "#1b7f4d",
    backgroundColor: "#e6f4ec",
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
  },
});

const DatenbereinigungPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const goBack = useSmartBack("/contacts");
  const { hasPermission, isAdmin } = useAuth();
  const [outlookReiter, setOutlookReiter] = useState(0);
  useEffect(() => {
    fetchOutlookReiter()
      .then((r) => setOutlookReiter(r.length))
      .catch(() => setOutlookReiter(0));
  }, []);
  // Merge braucht contacts:update — nur dann den „Zusammenführen"-Button zeigen.
  const canMerge = hasPermission("contacts:update");

  const steps = [
    {
      key: "duplikate",
      title: "Duplikate bereinigen",
      desc: "Wahrscheinliche Doppelgänger im Kontaktstamm prüfen und zusammenführen.",
      icon: <PeopleSwap20Regular style={{ fontSize: 24 }} />,
      to: "/contacts/duplicates",
    },
    {
      key: "ansprechpartner",
      title: "Ansprechpartner bereinigen",
      desc: "Status und Rollen der Firmen-Kontaktpersonen korrigieren (Interessent/Mensch/GF/Gesellschafter/Mitarbeiter).",
      icon: <PersonBoard20Regular style={{ fontSize: 24 }} />,
      to: "/datenbereinigung/ansprechpartner",
    },
    {
      key: "haushalte",
      title: "Haushalte bereinigen",
      desc: "Privatpersonen einer Anschrift zu Haushalten zusammenführen, Rolle im Haushalt vergeben und die Beziehungen (Ehepartner/Eltern/Kind …) anlegen.",
      icon: <Home20Regular style={{ fontSize: 24 }} />,
      to: "/datenbereinigung/haushalte",
    },
    {
      key: "unternehmensstrukturen",
      title: "Unternehmensstrukturen",
      desc: "Firmen an gleicher Anschrift zu Konzern- und Beteiligungsstrukturen verknüpfen (Tochter/Mutter/Schwester) oder als „gehört nicht dazu“ markieren.",
      icon: <BuildingMultiple20Regular style={{ fontSize: 24 }} />,
      to: "/datenbereinigung/unternehmensstrukturen",
    },
    {
      key: "namenszusatz",
      title: "Namenszusätze bereinigen",
      desc: "Kontakte mit Namenszusatz aus der Datenübernahme prüfen: Name, Vorname und Zusatz nebeneinander korrigieren — oder unverändert abhaken.",
      icon: <TextboxAlignBottomRotate9020Regular style={{ fontSize: 24 }} />,
      to: "/datenbereinigung/namenszusatz",
    },
    // Kontaktdaten bereinigen nur für admin (User 02.10.2026); „Arbeitgeber
    // zuordnen“ ist aus der Übersicht genommen.
    ...(isAdmin
      ? [
          {
            key: "kontaktdaten",
            title: "Kontaktdaten bereinigen",
            desc: "E-Mail-Adressen an der richtigen Stelle: Zentrale-Mails vom Ansprechpartner zur Firma, personenhafte Mails zum Ansprechpartner, Platzhalter löschen.",
            icon: <MailMultiple20Regular style={{ fontSize: 24 }} />,
            to: "/datenbereinigung/kontaktdaten",
          },
        ]
      : []),
    // Outlook-Importe: nur, wenn der Nutzer mindestens einen Reiter sehen darf.
    ...(outlookReiter > 0
      ? [
          {
            key: "outlook",
            title: "Outlook-Importe",
            desc: "Importierte Outlook-Kontakte mit bestehenden zusammenführen, zuweisen oder importieren — bis dahin fehlen sie in der Kontakttabelle.",
            icon: <MailInbox20Regular style={{ fontSize: 24 }} />,
            to: "/datenbereinigung/outlook-importe",
          },
        ]
      : []),
    // Altkunden ohne Duplikat prüfen — gleiche Berechtigung wie die Route
    // (/datenbereinigung/altkunden, requiredPermission "contacts:update"), damit
    // berechtigte Nicht-admin/gfgl-Benutzer (z. B. CLL/SAH) die Kachel sehen.
    ...(hasPermission("contacts:update")
      ? [
          {
            key: "altkunden",
            title: "Altkunden bereinigen",
            desc: "Kontakte mit Status Altkunde ohne offenes Duplikat durchgehen: alle Stammdaten ansehen, Status ändern oder als Mensch fürs/im Unternehmen klassifizieren.",
            icon: <History20Regular style={{ fontSize: 24 }} />,
            to: "/datenbereinigung/altkunden",
          },
        ]
      : []),
  ];

  // --- Übersicht: Suche + paginierte Liste ---
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [rows, setRows] = useState<DatenbereinigungRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Aufklappbare Treffer: Person → Haushalt-Kandidaten, Firma → Ansprechpartner (lazy).
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [related, setRelated] = useState<
    Record<number, DatenbereinigungRelated | "loading">
  >({});
  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setRelated((prev) => {
      if (prev[id]) return prev; // schon geladen / am Laden
      void fetchDatenbereinigungRelated(id)
        .then((res) => setRelated((p) => ({ ...p, [id]: res })))
        .catch(() =>
          setRelated((p) => ({ ...p, [id]: { kind: "none", members: [] } })),
        );
      return { ...prev, [id]: "loading" };
    });
  };

  // --- Mehrfach-Auswahl der Treffer (für „Zusammenführen") ---
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allOn = rows.length > 0 && rows.every((r) => selectedIds.has(r.contactId));
  const toggleAll = () =>
    setSelectedIds(() =>
      allOn ? new Set<number>() : new Set(rows.map((r) => r.contactId)),
    );
  const handleMerge = () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) return;
    navigate(`/contacts/merge?clusterIds=${ids.join(",")}`);
  };

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (query: string, offset: number) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (offset === 0) setLoading(true);
    try {
      const { rows: page, total: tot } = await listDatenbereinigung(
        query || undefined,
        PAGE_SIZE,
        offset,
        ac.signal,
      );
      setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
      setTotal(tot);
      setHasMore(offset + page.length < tot);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        setHasMore(false);
      }
    } finally {
      if (offset === 0) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auswahl beim Such-Wechsel leeren → keine „Geister-IDs" aus alter Suche.
    setSelectedIds(new Set());
    // Such-getrieben: ohne Begriff keine Liste (nur Hinweis + Modul-Kacheln).
    if (!qDebounced) {
      abortRef.current?.abort();
      setRows([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    void load(qDebounced, 0);
    return () => abortRef.current?.abort();
  }, [qDebounced, load]);

  const fetchMore = useCallback(() => {
    void load(qDebounced, rows.length);
  }, [load, qDebounced, rows.length]);

  const searchName = (r: DatenbereinigungRow) =>
    (r.lastname || r.firstName || "").trim();

  const goto = (path: string, r: DatenbereinigungRow) => {
    const name = searchName(r);
    navigate(name ? `${path}?q=${encodeURIComponent(name)}` : path);
  };

  const memberName = (m: DatenbereinigungMember) =>
    [m.lastname, m.firstName].filter(Boolean).join(", ") ||
    m.firstName ||
    `#${m.contactId ?? "?"}`;

  const renderRelated = (
    rel: DatenbereinigungRelated | "loading" | undefined,
    type: string | null,
  ) => {
    if (rel === undefined || rel === "loading")
      return (
        <span className={styles.expandHint}>
          <Spinner size="tiny" /> Lädt…
        </span>
      );
    if (rel.kind === "household") {
      if (!rel.members.length)
        return (
          <span className={styles.expandHint}>Keine Haushaltsmitglieder.</span>
        );
      return (
        <>
          <div className={styles.expandTitle}>Haushaltsmitglieder</div>
          {rel.members.map((m, i) => (
            <div
              key={m.candidateId ?? m.contactId ?? i}
              className={styles.memberRow}
            >
              <span>{memberName(m)}</span>
              {m.householdRole && (
                <span className={styles.memberMeta}>· {m.householdRole}</span>
              )}
              {m.kundennummer && (
                <span className={styles.memberMeta}>· Kd.-Nr. {m.kundennummer}</span>
              )}
              {m.bereinigtAt && (
                <span className={styles.bereinigtBadge}>bereinigt</span>
              )}
            </div>
          ))}
        </>
      );
    }
    if (rel.kind === "ansprechpartner") {
      if (!rel.members.length)
        return <span className={styles.expandHint}>Keine Menschen fürs Unternehmen.</span>;
      return (
        <>
          <div className={styles.expandTitle}>Menschen fürs Unternehmen</div>
          {rel.members.map((m, i) => (
            <div key={m.contactId ?? i} className={styles.memberRow}>
              <span>{memberName(m)}</span>
              {(m.relationType || m.function) && (
                <span className={styles.memberMeta}>
                  · {[m.relationType, m.function].filter(Boolean).join(" / ")}
                </span>
              )}
              {m.kundennummer && (
                <span className={styles.memberMeta}>· Kd.-Nr. {m.kundennummer}</span>
              )}
              {m.bereinigtAt && (
                <span className={styles.bereinigtBadge}>bereinigt</span>
              )}
            </div>
          ))}
        </>
      );
    }
    return (
      <span className={styles.expandHint}>
        {type === "Firma"
          ? "Keine Menschen fürs Unternehmen."
          : "Keine Haushaltsmitglieder."}
      </span>
    );
  };

  const renderRow = (r: DatenbereinigungRow) => {
    const name =
      [r.lastname, r.firstName].filter(Boolean).join(", ") ||
      r.firstName ||
      "(ohne Namen)";
    const addr = [r.street, [r.postalCode, r.city].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ");
    const canExpand = r.type === "Person" || r.type === "Firma";
    const isOpen = expanded.has(r.contactId);
    return (
      <div key={r.contactId} className={styles.rowWrap}>
        <div className={styles.row}>
          <span
            className={styles.rowCheck}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selectedIds.has(r.contactId)}
              onChange={() => toggle(r.contactId)}
              aria-label="Kontakt auswählen"
            />
          </span>
          {canExpand ? (
            <button
              className={styles.expandBtn}
              onClick={() => toggleExpand(r.contactId)}
              aria-label={isOpen ? "Einklappen" : "Ausklappen"}
            >
              {isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
            </button>
          ) : (
            <span className={styles.expandSpacer} />
          )}
          <div className={styles.rowBody}>
            <div>
              <span className={styles.rowName}>{name}</span>
              {r.type && <span className={styles.typeBadge}>{contactTypeLabel(r.type)}</span>}
            </div>
            <span className={styles.rowMeta}>
              {[r.kundennummer ? `Kd.-Nr. ${r.kundennummer}` : null, addr || null]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
          <div className={styles.chips}>
            {r.inAnsprechpartner && (
              <button
                className={styles.chip}
                onClick={() => goto("/datenbereinigung/ansprechpartner", r)}
              >
                <PersonBoard20Regular style={{ fontSize: 14 }} /> Ansprechpartner
              </button>
            )}
            {r.inHaushalt && (
              <button
                className={styles.chip}
                onClick={() => goto("/datenbereinigung/haushalte", r)}
              >
                <Home20Regular style={{ fontSize: 14 }} /> Haushalt
              </button>
            )}
            {r.inDuplikate && (
              <button
                className={styles.chip}
                onClick={() => goto("/contacts/duplicates", r)}
              >
                <PeopleSwap20Regular style={{ fontSize: 14 }} /> Duplikate
              </button>
            )}
          </div>
        </div>
        {isOpen && (
          <div className={styles.expandPanel}>
            {renderRelated(related[r.contactId], r.type)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zu Kontakte
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <BroomSparkle20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Datenbereinigung</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Alle Bereinigungsschritte für einen sauberen Datenstamm an einem Ort.
        </span>
      </div>

      <div className={styles.grid}>
        {steps.map((s) => (
          <button key={s.key} className={styles.card} onClick={() => navigate(s.to)}>
            <span className={styles.cardIcon}>{s.icon}</span>
            <span className={styles.cardBody}>
              <span className={styles.cardTitle}>{s.title}</span>
              <span className={styles.cardDesc}>{s.desc}</span>
            </span>
            <ChevronRight20Regular className={styles.chevron} />
          </button>
        ))}
      </div>

      <div className={styles.divider} />

      <div className={styles.searchRow}>
        <Search20Regular className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          type="search"
          placeholder="Kontakt suchen (Name oder Kd.-Nr.) – Haushalt/Ansprechpartner & offene Bereinigungen …"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {q && (
          <button
            className={styles.clearBtn}
            onClick={() => setQ("")}
            aria-label="Suche zurücksetzen"
          >
            <Dismiss16Regular />
          </button>
        )}
      </div>

      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>Gefundene Kontakte</h2>
        {total > 0 && (
          <span className={styles.countChip}>
            {total} {total === 1 ? "Kontakt" : "Kontakte"}
          </span>
        )}
        {rows.length > 0 && (
          <span className={styles.selectAll}>
            <Checkbox
              checked={allOn}
              onChange={toggleAll}
              aria-label="Alle auswählen"
              label="Alle auswählen"
            />
          </span>
        )}
        {selectedIds.size > 0 && (
          <span className={styles.selectionCount}>
            {selectedIds.size} ausgewählt
          </span>
        )}
        {canMerge && selectedIds.size >= 2 && (
          <button className={styles.mergeBtn} onClick={handleMerge}>
            <BranchCompare20Regular style={{ fontSize: 16 }} /> Zusammenführen
          </button>
        )}
      </div>

      <div id="scrollableDatenbereinigung" className={styles.scrollBox}>
        {loading ? (
          <p className={styles.empty}>Lade Kontakte…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced
              ? "Kein Kontakt passt zur Suche."
              : "Tippe oben, um einen Kontakt zu suchen."}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div className={styles.loadMore}>Lade weitere Kontakte…</div>}
            scrollableTarget="scrollableDatenbereinigung"
            style={{ overflow: "visible" }}
          >
            {rows.map((r) => renderRow(r))}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default DatenbereinigungPage;
